import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { onboardingDesignPLPRoute } from '../helpers/routes';

const getMeta = (name: string) => document.head.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;

const upsertMeta = (name: string, content: string) => {
  let tag = getMeta(name);

  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute('name', name);
    document.head.appendChild(tag);
  }

  tag.setAttribute('content', content);
};

const removeMeta = (name: string) => {
  const tag = getMeta(name);
  tag?.remove();
};

export type RouteMetaProps = {
  title?: string;
  description?: string;
  robots?: string;
  matchPath?: string;
};

const RouteMeta: React.FC<RouteMetaProps> = ({
  title = 'Wedding Website Designs | The Knot',
  description = 'Browse modern wedding website designs and choose a style that fits your celebration.',
  robots = 'index,follow',
  matchPath = onboardingDesignPLPRoute,
}) => {
  const location = useLocation();

  useEffect(() => {
    const matchesRoute = location.pathname === matchPath || location.pathname.startsWith(`${matchPath}/`);

    if (!matchesRoute) {
      return;
    }

    const previousTitle = document.title;
    const previousDescription = getMeta('description')?.content || '';
    const previousRobots = getMeta('robots')?.content || '';

    document.title = title;
    upsertMeta('description', description);
    upsertMeta('robots', robots);

    return () => {
      document.title = previousTitle;

      if (previousDescription) {
        upsertMeta('description', previousDescription);
      } else {
        removeMeta('description');
      }

      if (previousRobots) {
        upsertMeta('robots', previousRobots);
      } else {
        removeMeta('robots');
      }
    };
  }, [location.pathname, title, description, robots, matchPath]);

  return null;
};

export default RouteMeta;
